import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { forkJoin, of } from 'rxjs'
import { catchError, concatMap, filter, map, switchMap, tap, toArray } from 'rxjs/operators'
import {
  AddCustomCode,
  AddCustomCodeComplete,
  DeleteCustomCode,
  DeleteCustomCodeComplete,
  GetAllCustomCodes,
  GetAllCustomCodesComplete,
  UpdateCustomCode,
  UpdateCustomCodeComplete,
} from '@tv3/store/custom-codes/custom-code.actions'
import { CustomCodeService } from '@tv3/store/custom-codes/custom-code.service'
import { CustomCode } from '@tv3/store/custom-codes/custom-code.model'
import * as R from 'ramda'
import { ActionFailed, DataEntityType, Toaster } from '@tokeet-frontend/tv3-platform'
import { RemoveTagFromAllEntitiesComplete } from '@tokeet-frontend/tags'

@Injectable()
export class CustomCodeEffects {
  allCustomCodes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetAllCustomCodes),
      switchMap(() =>
        this.customCodes.getAll().pipe(
          map((customCodes) => GetAllCustomCodesComplete({ codes: customCodes })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  deleteCustomCodes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteCustomCode),
      switchMap(({ ids }) => {
        return of(...ids).pipe(
          concatMap((id) => this.customCodes.delete(id, true)),
          toArray(),
          tap(() => this.toaster.success($localize`Custom Code(s) deleted successfully!`)),
          map(() => DeleteCustomCodeComplete({ ids }))
        )
      }),
      catchError((error) => of(ActionFailed({ error })))
    )
  )

  addCustomCodes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddCustomCode),
      switchMap(({ request }) =>
        this.customCodes.create(request).pipe(
          map((code) => AddCustomCodeComplete({ code })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  updateCustomCodes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateCustomCode),
      switchMap(({ request }) =>
        this.customCodes.edit(request.pkey, request).pipe(
          map((code) => UpdateCustomCodeComplete({ update: { id: code.pkey, changes: code } })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  // Reload custom codes so their cached attributes.tags drop a tag removed from all codes.

  reloadOnBulkTagRemove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoveTagFromAllEntitiesComplete),
      filter(({ entityType }) => entityType === DataEntityType.CustomCode),
      map(() => GetAllCustomCodes())
    )
  )

  constructor(private actions$: Actions, private toaster: Toaster, private customCodes: CustomCodeService) {}
}
